/**
 * LOGOS — Supabase Client
 * Centralized Supabase client for auth and data operations.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const SUPABASE_URL = 'https://qfqgplopnwxilnyeajbt.supabase.co';
const SUPABASE_ANON_KEY = 'sb_publishable_TkLBuPIcDQMzIrdn3Z5g6g_7ZuTKXoo';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,       // Uses localStorage — survives tab close
    detectSessionInUrl: true,   // Handles OAuth redirect automatically
  },
});

/**
 * Get the correct redirect URL based on platform.
 * Native: custom URL scheme → deep link back to app
 * Web: window.location.origin
 */
function getRedirectUrl() {
  if (Capacitor.isNativePlatform()) {
    return 'com.cadenastrategic.logos://login-callback';
  }
  return window.location.origin;
}

/**
 * Handle OAuth on native: open browser, then close it when callback arrives.
 */
async function nativeOAuth(provider, extraOptions = {}) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: getRedirectUrl(),
      skipBrowserRedirect: true,
      ...extraOptions,
    },
  });
  if (error) throw error;
  if (data?.url) {
    await Browser.open({ url: data.url, windowName: '_self' });
  }
  return data;
}

/**
 * Sign in with Google OAuth via Supabase.
 * Native: opens in-app browser with deep link callback.
 * Web: redirects to Google, then back to the app.
 */
export async function signInWithGoogle() {
  if (Capacitor.isNativePlatform()) {
    return nativeOAuth('google', {
      scopes: 'openid email profile https://www.googleapis.com/auth/calendar.events',
    });
  }
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
      scopes: 'openid email profile https://www.googleapis.com/auth/calendar.events',
    },
  });
  if (error) throw error;
  return data;
}

/**
 * Sign in with Apple (required for App Store).
 */
export async function signInWithApple() {
  if (Capacitor.isNativePlatform()) {
    return nativeOAuth('apple');
  }
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: window.location.origin,
    },
  });
  if (error) throw error;
  return data;
}

/**
 * Sign out and clear session.
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get current authenticated user.
 * Returns null if not logged in.
 */
export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Get current session (includes access token for API calls).
 */
export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Get user profile from profiles table.
 */
export async function getProfile() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    console.warn('[Supabase] Profile fetch error:', error.message);
    return null;
  }
  return data;
}

/**
 * Save a session snapshot to the sessions table.
 */
export async function saveSession(sessionData) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.warn('[Supabase] No user, saving to localStorage only');
    return null;
  }

  const { data, error } = await supabase
    .from('sessions')
    .insert({
      user_id: user.id,
      channel: 'web',
      ...sessionData,
    })
    .select('id, start_time, lambda, verdict')
    .single();

  if (error) {
    console.error('[Supabase] Session save error:', error.message);
    return null;
  }

  // Upsert daily snapshot
  const sessionDate = new Date(sessionData.start_time || Date.now()).toISOString().split('T')[0];
  await supabase.rpc('upsert_daily_snapshot', {
    p_user_id: user.id,
    p_date: sessionDate,
    p_lambda: sessionData.lambda,
    p_omega: sessionData.omega,
    p_viability: sessionData.viability,
    p_entropy: sessionData.entropy,
    p_coherence: sessionData.coherence,
    p_verdict: sessionData.verdict,
  }).catch(e => console.warn('[Supabase] Daily snapshot error:', e.message));

  return data;
}

/**
 * Get user sessions (paginated).
 */
export async function getSessions(limit = 50, offset = 0) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error, count } = await supabase
    .from('sessions')
    .select('*', { count: 'exact' })
    .eq('user_id', user.id)
    .order('start_time', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('[Supabase] Sessions fetch error:', error.message);
    return null;
  }

  return { sessions: data, total: count };
}

/**
 * Get longitudinal report data.
 */
export async function getLongitudinalReport() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('daily_snapshots')
    .select('*')
    .eq('user_id', user.id)
    .order('date', { ascending: true });

  if (error) return null;
  return { snapshots: data };
}

/**
 * Delete user account and all associated data.
 * Deletes from: sessions, daily_snapshots, profiles, then auth user.
 * Also clears M5 PostgreSQL data via API.
 */
export async function deleteAccount() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const userId = user.id;

  // Delete from Supabase tables (order matters: children first)
  await supabase.from('emergence_events').delete().eq('user_id', userId);
  await supabase.from('weight_history').delete().eq('user_id', userId);
  await supabase.from('protocol_daily').delete().eq('user_id', userId);
  await supabase.from('study_participants').delete().eq('user_id', userId);
  await supabase.from('daily_snapshots').delete().eq('user_id', userId);
  await supabase.from('sessions').delete().eq('user_id', userId);
  await supabase.from('profiles').delete().eq('id', userId);

  // Delete from M5 PostgreSQL via API
  const dbUserId = sessionStorage.getItem('logos-db-user-id');
  if (dbUserId) {
    try {
      await fetch(`/api/user/${dbUserId}`, { method: 'DELETE' });
    } catch (e) {
      console.warn('[Supabase] M5 user delete failed:', e.message);
    }
  }

  // Clear all local storage
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('logos-')) keysToRemove.push(key);
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
  sessionStorage.clear();

  // Sign out (this also clears the Supabase session)
  await supabase.auth.signOut();
}

export default supabase;
